import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { exhaustMap, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { CalendarConnectData, CalendarServiceModel } from '../../../shared/models/user/user.model';
import * as LocaleActions from '../../locale/actions/locale.action';
import { IAppState } from '../../index';
import * as CompanyActions from '../../company/actions/company.action';
import * as UserActions from '../actions/user.action';
import { UserService } from '../services/user.service';
import { CalendarService } from '../../../shared/services/google/calendar.service';
import { AuthActions } from '../../../auth';
import { AuthService } from '../../../auth/services/auth.service';
import * as ModalActions from '../../modal/actions/modal.action';
import { ToastComponent } from '../../../ui/toast/toast.component';
import { ErrorHandlingService } from '../../../shared/services/error-handling/error-handling.service';



@Injectable()
export class UserEffects {
    private readonly actions$ = inject(Actions);
    private readonly userService = inject(UserService);
    private readonly store = inject(Store<IAppState>);
    private readonly calendarService = inject(CalendarService);
    private readonly authService = inject(AuthService);
    private readonly errorService = inject(ErrorHandlingService);

    fetchCurrentUser$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.fetch),
        withLatestFrom(this.store),
        exhaustMap(([action, state]) => {
          if (state.user.current && !action.force) {
            return of(
              UserActions.changed({
                current: { ...state.user.current },
                errors: [],
              })
            );
          }

          return this.userService
            .getCurrentUser(action.force).pipe(
              mergeMap(user => {
                return [
                  LocaleActions.set({ locale: user.user_profile.language.toLowerCase() }),
                  UserActions.changed({
                    current: user,
                    errors: [],
                  }),
                ];
              }),
              catchError(error => of(UserActions.apiError({
                name: 'fetchCurrentUser',
                error
              })))
            );
        })
      ));

    load$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.load),
        tap(() => UserActions.changed({ errors: [] })),
        switchMap((action) => {
          const token = { access: action.access, refresh: action.refresh };

          this.authService.setTokenToStorage(token);

          return this.userService
            .getCurrentUser().pipe(
              map(user => {
                return AuthActions.loginSuccess({
                  access: token?.access,
                  refresh: token?.refresh,
                  user: user
                });
              }),
              catchError(error => {
                return of(UserActions.apiError({
                  name: 'load',
                  error
                }));
              }));
        })
      ));

    patch_settings$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.patchSettings),
        withLatestFrom(this.store),
        filter(([, state]) => !!state.user.current),
        switchMap(([action, state]) => {
          return this.userService
            .patchSettings(state.user.current.id, action).pipe(
              map(settings => {
                return UserActions.changed({ current: {
                    ...state.user.current,
                    user_settings: settings
                  } });
              }),
              tap(() => {
                // this.store.dispatch(new NotificationActions.FetchAction());
              }),
              catchError(error => of(UserActions.apiError({
                name: 'patchSettings',
                error
              }))));
        })
      ));

    changeLanguage$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.changeLanguage),
        withLatestFrom(this.store),
        filter(([, state]) => !!state.user.current),
        filter(([action, state]) => action.language !== state.user.current.user_profile.language),
        switchMap(([action]) => {
          return this.userService.setCurrentLanguage(action.language).pipe(
            map(res => {
              return UserActions.changed({
                current: res,
                errors: [],
              });
            }),
            catchError(error => of(UserActions.apiError({
              name: 'changeLanguage',
              error
            })))
          );
        })
      ));

    create$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.create),
        tap(() => UserActions.changed({ errors: [] })),
        switchMap((action) => {
          return this.userService
            .createUser(action.user).pipe(
              map(() => {
                const minAttemptsWarning = 5; // Number of attempts to show warning message
                const maxAttempts = 7; // Number of attempts to block the form
                const attemptsName = 'createApplicantNumber';

                this.errorService.handleAttemptsNumber({
                  endpointName: attemptsName,
                  minAttemptsWarning,
                  maxAttempts,
                  reset: true
                });

                return AuthActions.login({
                  username: action.user.email,
                  password: action.user.password,
                  redirect: false,
                  attemptsName: attemptsName
                });
              }),
              catchError(error => of(UserActions.apiError({
                name: 'create',
                error
              }))));
        })
      ));

    logout$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.logout),
        withLatestFrom(this.store),
        mergeMap(([action]) => {
          this.authService.clearAuthFromStorage();

          const response: ReturnType<typeof UserActions.changed | typeof UserActions.apiError | typeof CompanyActions.changed>[] = [
            UserActions.changed({
              current: null,
              errors: [],
            }),
            CompanyActions.changed({
              current: null,
              errors: [],
            }),
          ];

          if (action.error) {
            response.push(
              UserActions.apiError({
                name: 'logout',
                error: action.error
              })
            );
          }

          return response;
        })
      ));

    apiError$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.apiError),
        withLatestFrom(this.store),
        map(([action, state]) => {
          return UserActions.changed({
            errors: [
              { name: action.name, error: action.error },
              ...(state.user.errors || [])
            ],
          });
        })
      ));

    connectCalendar$ = createEffect(() => this.actions$.pipe(
      ofType(UserActions.connectCalendar),
      withLatestFrom(this.store),
      switchMap(([action]) =>
        (action.service === CalendarServiceModel.GOOGLE ?
            this.calendarService.connectToGoogle() :
            this.calendarService.connectToOutlook()
        ).pipe(
          map((res: CalendarConnectData) => {
            window.open(res.authorization_uri, '_self');
            return UserActions.connectCalendarSuccess({ service: action.service });
          }),
          catchError(error => {
            this.store.dispatch(
              ModalActions.open({
                cmpType: ToastComponent,
                props: {
                  view: 'error',
                  title: `Error when connecting Google calendar: ${error.statusText}`
                },
              }));

            return of(UserActions.connectCalendarFailure({ service: action.service }));
          })
        )
      )
    ));

    disconnectCalendar$ = createEffect(() => this.actions$.pipe(
      ofType(UserActions.disconnectCalendar),
      withLatestFrom(this.store),
      switchMap(([action, state]) =>
        (action.service === CalendarServiceModel.GOOGLE ?
            this.calendarService.disconnectFromGoogle() :
            this.calendarService.disconnectFromOutlook()
        ).pipe(
          map(() => {
            this.store.dispatch(
              ModalActions.open({
                cmpType: ToastComponent,
                props: {
                  view: 'success',
                  title: 'COMMON.SAVED'
                },
              }));

            return UserActions.changed({
              current: {
                ...state.user.current,
                user_settings: {
                  ...state.user.current.user_settings,
                  google: null,
                  microsoft: null,
                  calendarLoad: {
                    ...state.user.current.user_settings.calendarLoad,
                    disconnecting: false
                  }
                }
              },
              errors: [],
            });
          }),
          catchError(error => {
            this.store.dispatch(
              ModalActions.open({
                cmpType: ToastComponent,
                props: {
                  view: 'error',
                  title: `Error when disconnecting Google calendar: ${error.statusText}`
                },
              }));

            return of(UserActions.disconnectCalendarFailure({ service: action.service }));
          })
        ))
    ));

    reconnectCalendar$ = createEffect(() => this.actions$.pipe(
      ofType(UserActions.reconnectCalendar),
      withLatestFrom(this.store),
      switchMap(([action, state]) =>
        (action.service === CalendarServiceModel.GOOGLE ?
            this.calendarService.disconnectFromGoogle({
              cancel_pending: false
            }) :
            this.calendarService.disconnectFromOutlook({
              cancel_pending: false
            })
        ).pipe(
          map(() => UserActions.connectCalendar({
            service: action.service
          })),
          catchError(error => {
            this.errorService.handleHttpErrors({
              errorResponse: error,
            });

            return of(UserActions.disconnectCalendarFailure({ service: action.service }));
          })
        ))
    ));

    /**
     * Commented out it because on impersonate page we don't need to get the user
     */
    // Any startWith observables - Should always BE LAST!
    // @Effect()
    // init$: Observable<Action> =
    //     this.actions$
    //         .pipe(ofType(UserActions.init),
    //             startWith(UserActions.init()),
    //             map(() => {
    //                 return UserActions.fetch({});
    //             }));
}
